/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useMemo, type RefObject } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@vaas/react-ui-kit';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/Icon';
import { useUserId } from '@/api/hooks';
import { getLanguages, saveUserSettings, type LanguageDto } from '@/api/settings';
import { SUPPORTED_LANGUAGES } from '@/i18n/i18n';
import { languageListLabel, sortLanguageList } from './languageList';
import { Menu } from './Menu';

/** Short badge shown on the trigger, e.g. EN, ES-MX, ZH-CN. */
const shortCode = (code: string) => code.toUpperCase();

type ViewTransitionDoc = Document & {
  startViewTransition?: (cb: () => void | Promise<void>) => { finished: Promise<void> };
};

/** Applies the language at runtime, using View Transitions when supported. */
async function applyLanguage(
  i18n: { changeLanguage: (code: string) => Promise<unknown> },
  code: string,
): Promise<void> {
  const start = (document as ViewTransitionDoc).startViewTransition;
  if (typeof start === 'function') {
    await start
      .call(document, async () => {
        await i18n.changeLanguage(code);
      })
      .finished.catch(() => {});
    return;
  }
  await i18n.changeLanguage(code);
}

/** Header language switcher: lists backend locales, applies via i18next, persists to settings. */
export function LanguageMenu({
  open,
  onOpenChange,
  handoffFrom,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The stand-in that opened the menu while its own trigger is hidden (see `Menu`). */
  handoffFrom?: RefObject<HTMLElement | null>;
}) {
  const { t, i18n } = useTranslation();
  const gmin = useUserId();
  const queryClient = useQueryClient();
  // The stored preference, not the resolved theme: saving the language must not quietly rewrite a
  // user's "system" choice into whichever of light or dark happened to be painted at the time.
  const { theme } = useTheme();

  const enabled = Boolean(gmin);
  const languagesQuery = useQuery({
    queryKey: ['languages'],
    queryFn: getLanguages,
    enabled,
    retry: false,
    staleTime: 300_000,
  });

  const current = i18n.language;
  // Memoize language options off stable query data.
  const languages = useMemo<LanguageDto[]>(() => {
    const backend = languagesQuery.data ?? [];
    return backend.length
      ? backend
      : SUPPORTED_LANGUAGES.map((code) => ({
          languageId: 0,
          languageCode: code,
          displayString: code,
        }));
  }, [languagesQuery.data]);

  const labelled = useMemo(
    () =>
      sortLanguageList(
        languages.map((l) => ({
          code: l.languageCode,
          label: languageListLabel(l.languageCode),
        })),
        current,
      ),
    [languages, current],
  );
  const currentLabel = labelled.find((l) => l.code === current)?.label ?? shortCode(current);

  // Persist eagerly so a quick refresh after switching cannot drop the save.
  const persistLanguage = (code: string) => {
    if (!gmin) return;
    void saveUserSettings(gmin, {
      language: code,
      theme,
    })
      .then(() => queryClient.invalidateQueries({ queryKey: ['user-settings', gmin] }))
      .catch(() => {
        /* persisting the language is best-effort; the runtime switch already applied */
      });
  };

  const select = (code: string) => {
    onOpenChange(false);
    if (code === current) return;
    persistLanguage(code);
    applyLanguage(i18n, code).catch(() => {
      /* the runtime switch failed (e.g. i18next threw); the previous language stays active */
    });
  };

  return (
    <Menu
      open={open}
      onOpenChange={onOpenChange}
      handoffFrom={handoffFrom}
      trigger={({ open: isOpen, toggle }) => (
        <button
          className="tb-lang"
          title={`${t('language.title')}: ${currentLabel}`}
          data-tour="language"
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-label={`${t('language.title')}: ${currentLabel}`}
          onClick={toggle}
        >
          <Icon name="globe" size={18} />
          <span className="tb-lang-code">{shortCode(current)}</span>
        </button>
      )}
    >
      <div className="lang-head">
        <span className="lang-head-icon">
          <Icon name="globe" size={20} />
        </span>
        <div className="lang-head-text">
          <div className="lang-head-title">{t('language.title')}</div>
          <div className="lang-head-current">{currentLabel}</div>
        </div>
      </div>
      <div className="lang-list" role="group" aria-label={t('language.title')}>
        {labelled.map((l) => {
          const selected = l.code === current;
          return (
            <button
              key={l.code}
              type="button"
              aria-current={selected ? 'true' : undefined}
              className={`lang-item${selected ? ' on' : ''}`}
              onClick={() => select(l.code)}
            >
              <span className="lang-item-flag">{shortCode(l.code)}</span>
              <span className="lang-item-text">
                <span className="lang-item-name">{l.label}</span>
              </span>
              {selected && (
                <span className="lang-item-check">
                  <Icon name="check" size={16} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </Menu>
  );
}
